import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import FeatureNotEnabled from '../components/FeatureNotEnabled';
import { featureFlagsService } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { 
  Truck, 
  Users, 
  Settings, 
  Clock,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

/**
 * Dispatch Automation Module (Phase 6 - Scaffolded)
 * 
 * FEATURE FLAG: dispatch_automation_enabled
 * ACCESS: Super Admin + Admin only
 * STATUS: Placeholder - No actual automation implemented
 */
const DispatchAutomationPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const allowedRoles = ['admin', 'super_admin', 'ops_admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkFeature = async () => {
      try {
        const enabledFeatures = await featureFlagsService.getEnabledFeatures();
        setFeatureEnabled(enabledFeatures.includes('dispatch_automation_enabled'));
      } catch (err) {
        console.error('Failed to check feature flag:', err);
        setFeatureEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      checkFeature();
    }
  }, [token]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return <FeatureNotEnabled featureName="Dispatch Automation" />;
  }

  // Placeholder content when feature IS enabled (future-ready scaffold)
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Dispatch Automation' }]} />

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-8 h-8 text-blue-600" />
            Dispatch Automation
          </h1>
          <p className="text-gray-600 mt-1">Automated driver assignments and dispatch workflows</p>
        </div>

        {/* Preview Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-800">Preview Mode</div>
            <div className="text-sm text-blue-700">
              This module is in preview mode. Features shown are placeholders for future development.
              No automation or data writes are active.
            </div>
          </div>
        </div>

        {/* Placeholder Sections */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Driver Assignments */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-500">
                <Users className="w-5 h-5" />
                Driver Assignments
              </CardTitle>
              <CardDescription>Coming Soon</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-400 space-y-2">
                <p>• Auto-assign drivers to loads</p>
                <p>• Skill and certification matching</p>
                <p>• Availability tracking</p>
              </div>
              <div className="mt-4 flex items-center text-gray-400 text-sm">
                <Clock className="w-4 h-4 mr-1" />
                Feature under development
              </div>
            </CardContent>
          </Card>

          {/* Dispatch Queue */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-500">
                <ArrowRight className="w-5 h-5" />
                Dispatch Queue
              </CardTitle>
              <CardDescription>Coming Soon</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-400 space-y-2">
                <p>• Priority-based queue management</p>
                <p>• Real-time dispatch status</p>
                <p>• Batch dispatch operations</p>
              </div>
              <div className="mt-4 flex items-center text-gray-400 text-sm">
                <Clock className="w-4 h-4 mr-1" />
                Feature under development
              </div>
            </CardContent>
          </Card>

          {/* Rules & Automation */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-500">
                <Settings className="w-5 h-5" />
                Rules & Automation
              </CardTitle>
              <CardDescription>Coming Soon</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-400 space-y-2">
                <p>• Custom dispatch rules</p>
                <p>• Trigger-based automation</p>
                <p>• Escalation workflows</p>
              </div>
              <div className="mt-4 flex items-center text-gray-400 text-sm">
                <Clock className="w-4 h-4 mr-1" />
                Feature under development
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DispatchAutomationPage;
